/**
 * Testmodus „Prüfregel testen" (#2356) – Vertrag zwischen Frontend und Backend.
 *
 * Feldnamen identisch mit den Java-Records in `Backend/.../workflows/ruletest/dto`. Plan:
 * `Backend/docs/umsetzungsplan-pruefregel-testlauf.md` §6. Änderungen nur mit Rückmeldung.
 */

/** IDS seit 2026-10-06: Prüfregel = Spezifikation, `ruleKey` = ihr Name, kein KI-Modell. */
export type RuleTestRulesetType = 'KI' | 'HYBRID' | 'CROSS' | 'IDS';
export type RuleTestStatus = 'RUNNING' | 'COMPLETED' | 'FAILED';
export type RuleTestFailureKind = 'TIMEOUT' | 'MODULE_ERROR' | 'START_ERROR';

export interface RuleTestQuota {
  /** Erlaubte Testläufe pro Tag (Server-Property, Standard 5). Nie im Frontend hartkodieren. */
  limit: number;
  /** Verbrauchte plus gerade laufende Testläufe des heutigen Tages. */
  used: number;
  remaining: number;
  /** ISO-8601 mit Offset: nächste 00:00 Europe/Berlin. */
  resetsAt: string;
  activeRunId: string | null;
}

export interface RuleTestFileRef {
  fileId: string;
  /** Nur bei CROSS: Key des Dateipools. */
  poolKey?: string;
}

export interface RuleTestStartRequest {
  rulesetType: RuleTestRulesetType;
  rulesetId: string;
  /** KI/CROSS: `rules[].id` im Snapshot (bei neuer Regel die Temp-ID); HYBRID: Definition-ID der Regel. */
  ruleKey: string;
  editorDirty: boolean;
  /** KI/HYBRID genau eine Datei, CROSS genau eine je Pool. */
  files: RuleTestFileRef[];
  /** KI/CROSS: Rückgabe von `buildKiDocumentSnapshot()`. */
  ki?: unknown;
  /** HYBRID: `buildSetConfig()` plus Manifest der Regel. */
  hybrid?: {setConfig: unknown; ruleManifest: unknown};
  /**
   * IDS: IDS-XML des Editor-Stands (`IdsAdapterService.toXml`), auf GENAU die Spezifikation `ruleKey`
   * reduziert (`filterSpecificationsByName`).
   */
  ids?: {xml: string};
}

export interface RuleTestProgress {
  /** 1-basiert. */
  stepIndex: number;
  stepCount: number;
  /** 0..100 */
  percent: number;
  label: string;
}

export interface RuleTestModuleTraceEntry {
  nodeId: string;
  label: string;
  moduleId: string;
  status: 'COMPLETED' | 'FAILED' | 'SKIPPED';
  branch?: string | null;
  element?: string | null;
  input?: unknown;
  output?: unknown;
}

export interface RuleTestFileView {
  fileId: string;
  fileName: string;
  poolKey?: string | null;
}

export interface RuleTestRunView {
  runId: string;
  status: RuleTestStatus;
  failureKind?: RuleTestFailureKind | null;
  /** Nutzertauglich, deutsch. */
  failureMessage?: string | null;
  rulesetType: RuleTestRulesetType;
  rulesetId: string;
  ruleKey: string;
  /** z. B. „GL_3_B · ASR A3.5 Raumtemperatur". */
  ruleLabel: string;
  files: RuleTestFileView[];
  model: string | null;
  editorDirty: boolean;
  /** ISO-8601 (UTC). */
  startedAt: string;
  finishedAt?: string | null;
  durationMs?: number | null;
  progress: RuleTestProgress;
  /** `workflow_instance.outputDataJson` unverändert – nur bei COMPLETED. */
  output?: unknown | null;
  /** Nur HYBRID. */
  moduleTrace?: RuleTestModuleTraceEntry[] | null;
  quota: RuleTestQuota;
}

/** Fehlerantwort der Testlauf-Endpunkte (422/429/409/404). */
export interface RuleTestErrorResponse {
  status: number;
  code: 'INVALID' | 'DAILY_LIMIT' | 'RUN_ACTIVE' | 'NOT_FOUND';
  message: string;
  errors?: string[];
  runId?: string;
  quota?: RuleTestQuota;
}

/** Ergebnis von `RuleTestService.start` – Fehler als Daten, nicht als Throw im Template. */
export type RuleTestStartResult =
  | {kind: 'started'; run: RuleTestRunView}
  | {kind: 'invalid'; errors: string[]}
  | {kind: 'limit'; quota: RuleTestQuota}
  | {kind: 'active'; runId: string}
  | {kind: 'error'; message: string};

// ─── Popup-Kontext (vom Editor gebaut, an app-rule-test-popup übergeben) ──────────────────────

/** Eine wählbare Prüfregel im Popup. */
export interface RuleTestRuleOption {
  /** Wird als `ruleKey` gesendet. */
  key: string;
  shortCode: string;
  name: string;
  /** Kanonische Dateitypen wie im Editor (`'PDF'`, `'Excel'`, …); leer = Set-Vorgabe gilt. */
  allowedFileTypes: string[];
  /** Klassen-IDs bzw. `seed:<KEY>`; leer = Set-Vorgabe gilt. */
  allowedDocumentClasses: string[];
}

/** Ein Dateipool beim Cross Check (Slice C). */
export interface RuleTestPoolOption {
  key: string;
  name: string;
  allowedFileTypes: string[];
  allowedDocumentClasses: string[];
}

export interface RuleTestPopupContext {
  projectId: number;
  /** Anzeige „Aus „{projectName}" · erlaubt: …". */
  projectName: string;
  rulesetType: RuleTestRulesetType;
  rulesetId: string;
  /** Anzeige „Aus Prüfregelset „{setLabel}" · n Prüfregeln". */
  setLabel: string;
  rules: RuleTestRuleOption[];
  preselectedRuleKey: string | null;
  /** Prüfregel-Select gesperrt (Workflow-Editor: die Regel ist fest). */
  ruleLocked: boolean;
  /** Set-Vorgaben, wenn eine Regel keine eigenen hat. */
  setAllowedFileTypes: string[];
  setAllowedDocumentClasses: string[];
  /** Nur CROSS. */
  pools?: RuleTestPoolOption[];
  editorDirty: boolean;
  /** Anzeigename des KI-Modells. */
  modelLabel: string | null;
  /** Blockierende Hinweise zum Editor-Stand, die für jede Regel gelten; leer = startbar. */
  validationErrors: string[];
  /**
   * Hinweise, die nur eine bestimmte Regel betreffen (Schlüssel = `RuleTestRuleOption.key`). Das
   * Popup zeigt sie nur, solange genau diese Regel gewählt ist – eine unvollständige Regel soll den
   * Test einer anderen nicht sperren.
   */
  ruleValidationErrors?: Record<string, string[]>;
  /** Schlüssel für Sitzungsergebnis, Toast und Punkt am Header-Button. */
  contextKey: string;
  /** Baut den Request aus dem (beim Öffnen eingefrorenen) Editor-Stand. */
  buildRequest: (ruleKey: string, files: RuleTestFileRef[]) => RuleTestStartRequest;
  /** Report-Konfiguration für die Result-Card (aus `buildDefaultReportConfigForRuleset`). */
  reportConfig: unknown;
  /**
   * Nur IDS: Farbschwellen des Editor-Stands (`currentThresholds()`), damit die Vorschau den Score so
   * einfärbt, wie es der Bericht mit diesem Stand täte.
   */
  idsThresholds?: unknown;
}

/** Sitzungsschlüssel eines Editor-Kontexts. */
export function ruleTestContextKey(projectId: number | string, rulesetId: string, ruleKey?: string | null): string {
  return ruleKey ? `${projectId}:${rulesetId}:${ruleKey}` : `${projectId}:${rulesetId}`;
}
